import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare, faCircleUp, faCircleDown, } from '@fortawesome/free-regular-svg-icons';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../../components/UI/Modal';
import { Code } from '../../components/UI/Code';
import { Loading } from '../../components/UI/Loading';
import { WordSearchForm } from '../../components/WordSearch/WordSearchForm';
import { WordItem } from '../../components/WordSearch/WordItem';
import { WordBox } from '../../components/WordSearch/WordBox';
import { Grid } from '../../components/WordSearch/Grid';
import { getWordSearch, updateWordSearch, updateWordSearchStatus, updateWords, deleteWordSearch, } from '../../redux/actions/word-searches';
import { clearError } from '../../redux/reducers/word-searches';
import { generateWordSearch, DIRECTIONS_ALL, DIRECTIONS_SIMPLE, } from '../../utils/word-search';
import { configService } from '../../services/config.service';
const { playServer } = await configService.appConfig;
const EditWordSearchComponent = ({ wordSearchDetail, getWordSearch, updateWordSearch, updateWordSearchStatus, updateWords, deleteWordSearch, clearError, loading, error, errorDetail, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isUpdatingWords, setIsUpdatingWords] = useState(false);
    const [generatedGrid, setGeneratedGrid] = useState(null);
    const [copied, setCopied] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const wordSearchId = params.id;
    /**
     * Effect to fetch wordSearch details if not already loaded
     * Cleans up error state on unmount
     */
    useEffect(() => {
        // Clear any previous errors and fetch wordSearch details
        if (wordSearchId) {
            clearError({});
            getWordSearch(wordSearchId);
        }
    }, [wordSearchId, getWordSearch, clearError]);
    /**
     * Regenerate the preview grid whenever the words (or the randomise-grid
     * setting) change. The grid itself is not persisted — only the word list is
     * — so it is derived here from the stored answers.
     */
    useEffect(() => {
        if (wordSearchDetail?.words?.length) {
            const directions = wordSearchDetail.settings.randomiseGrid
                ? DIRECTIONS_ALL
                : DIRECTIONS_SIMPLE;
            setGeneratedGrid(generateWordSearch(wordSearchDetail.words.map((w) => w.answer), { directions }));
        }
        else {
            setGeneratedGrid(null);
        }
    }, [wordSearchDetail?.words, wordSearchDetail?.settings.randomiseGrid]);
    // Guard placed after all hooks so hook order stays stable across renders
    if (!wordSearchId)
        return React.createElement("div", null, "WordSearch not found");
    // useEffect(() => {
    //   if (wordSearchDetail?.seed) {
    //     const gridData = generateGrid(wordSearchDetail.seed);
    //     setGeneratedGrid({
    //       cells: gridData.newGrid,
    //       length: gridData.length,
    //       height: gridData.height,
    //     });
    //   } else {
    //     setGeneratedGrid(null);
    //   }
    // }, [wordSearchDetail?.seed, wordSearchDetail?.settings.randomiseGrid]);
    const handleCodeCopy = () => {
        setCopied(true);
    };
    const handleModalOpen = (event) => {
        event.preventDefault();
        setIsModalOpen(true);
        setCopied(false);
    };
    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    const handleStatusUpdate = (event) => {
        event.preventDefault();
        if (wordSearchDetail) {
            updateWordSearchStatus({
                wordSearchId,
                status: wordSearchDetail.status === 'draft' ? 'published' : 'draft',
            });
        }
    };
    /**
     * Additional check to ensure we have the correct wordSearch loaded
     * If the loaded wordSearch doesn't match the current wordSearchId, show loading
     */
    const isCorrectWordSearchLoaded = wordSearchDetail && wordSearchDetail.wordSearchId === wordSearchId;
    const handleWordSearchUpdate = (wordSearchData) => {
        if (wordSearchDetail) {
            updateWordSearch({
                wordSearchId: wordSearchDetail.wordSearchId,
                data: {
                    name: wordSearchData.name,
                    duration: wordSearchData.duration,
                    settings: wordSearchData.settings,
                },
            });
        }
        setIsEditing(false);
    };
    /**
     * Handles edit cancellation
     * Exits edit mode without saving changes
     */
    const handleEditCancel = () => {
        setIsEditing(false);
    };
    /**
     * Handles wordSearch deletion
     * Deletes the wordSearch and navigates back to list
     */
    const handleWordSearchDelete = () => {
        if (wordSearchDetail) {
            deleteWordSearch(wordSearchDetail.wordSearchId);
            navigate('/word-searches');
        }
    };
    /**
     * Handles words update
     * @param {Array<Word>} words - Updated words array
     */
    const handleUpdateWords = (words
    // seed?: Seed
    ) => {
        if (wordSearchDetail) {
            updateWords({ wordSearchId: wordSearchDetail.wordSearchId, words });
            // if (seed) {
            //   const gridData = generateGrid(seed);
            //   setGeneratedGrid({
            //     cells: gridData.newGrid,
            //     length: gridData.length,
            //     height: gridData.height,
            //   });
            // }
        }
        setIsUpdatingWords(false);
    };
    /**
     * Handles word edit cancellation
     * Exits word edit mode without saving changes
     */
    const handleEditWordsCancel = () => {
        setIsUpdatingWords(false);
    };
    // Show loading while fetching data
    if (loading)
        return React.createElement(Loading, null);
    // Show error message if there's an API error
    if (error) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "Error loading wordSearch"),
            React.createElement("p", null, errorDetail ||
                'Failed to load wordSearch details. Please try again.'),
            React.createElement("button", { className: "btn--primary", onClick: () => {
                    clearError({});
                    getWordSearch(wordSearchId);
                } }, "Retry")));
    }
    // Show error if wordSearch not found after successful API call
    if (!loading && !wordSearchDetail) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "WordSearch not found"),
            React.createElement("p", null, "The wordSearch you're looking for doesn't exist or has been deleted."),
            React.createElement("button", { className: "btn--primary", onClick: () => window.history.back() }, "Go Back")));
    }
    // Show loading if we don't have the correct wordSearch loaded yet
    if (!isCorrectWordSearchLoaded)
        return React.createElement(Loading, null);
    return (React.createElement(React.Fragment, null,
        wordSearchDetail && wordSearchDetail.code && (React.createElement(Modal, { id: wordSearchDetail.code, component: Code, componentProps: {
                id: wordSearchDetail.code,
                link: `${playServer}?code=${wordSearchDetail.code}`,
                copied,
                onCopy: handleCodeCopy,
            }, isOpen: isModalOpen, onClose: handleModalClose })),
        wordSearchDetail && (React.createElement("div", { className: "activity-container" },
            isEditing ? (React.createElement(WordSearchForm, { wordSearch: wordSearchDetail, onSubmit: handleWordSearchUpdate, onCancel: handleEditCancel })) : (React.createElement("div", { className: "activity-item-no-hover" },
                React.createElement("div", { className: "activity-header" },
                    React.createElement("h2", null, wordSearchDetail.name)),
                React.createElement("div", { className: "activity-settings" },
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: wordSearchDetail.settings.timed === true
                                ? faSquareCheck
                                : faSquare }),
                        "Timed"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: wordSearchDetail.settings.randomiseGrid === true
                                ? faSquareCheck
                                : faSquare }),
                        "Randomise Grid"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: wordSearchDetail.settings.sendResultEmail === true
                                ? faSquareCheck
                                : faSquare }),
                        "Send Result Email"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: wordSearchDetail.settings.public === true
                                ? faSquareCheck
                                : faSquare }),
                        "Share Publicly")),
                wordSearchDetail.duration && (React.createElement("div", { className: "activity-meta" },
                    React.createElement("span", null,
                        "Duration: ",
                        parseInt(wordSearchDetail.duration) / 60,
                        ' ',
                        "min",
                        ' '))),
                React.createElement("div", { className: "activity-actions" },
                    React.createElement("button", { className: "btn--primary", onClick: () => {
                            setIsEditing(true);
                        }, disabled: wordSearchDetail.status === 'published' }, "Edit"),
                    React.createElement("button", { className: "btn--primary publish", onClick: handleStatusUpdate, disabled: wordSearchDetail.words.length === 0 },
                        React.createElement(FontAwesomeIcon, { icon: wordSearchDetail.status === 'draft'
                                ? faCircleUp
                                : faCircleDown }),
                        wordSearchDetail.status === 'draft'
                            ? 'Publish'
                            : 'Unpublish'),
                    React.createElement("button", { className: "btn--secondary share", value: wordSearchDetail.code, onClick: handleModalOpen, disabled: wordSearchDetail.status === 'draft' },
                        React.createElement(FontAwesomeIcon, { icon: faShareNodes }),
                        "Host"),
                    React.createElement("button", { className: "btn--delete", onClick: handleWordSearchDelete, disabled: wordSearchDetail.status === 'published' }, "Delete")))),
            wordSearchDetail.words && wordSearchDetail.words.length > 0 ? (isUpdatingWords ? (React.createElement(WordBox, { randomiseGrid: wordSearchDetail.settings.randomiseGrid, words: wordSearchDetail.words, 
                // seed={wordSearchDetail.seed}
                onUpdate: handleUpdateWords, onCancel: handleEditWordsCancel })) : (React.createElement("div", { className: "word-item__container" },
                React.createElement("div", { className: "word-item__content" },
                    React.createElement("div", { className: "word-item__words" },
                        React.createElement("div", { className: "word-item__row" },
                            React.createElement("div", { className: "word-item__answer--heading" },
                                React.createElement("h4", null, "ANSWER")),
                            React.createElement("div", { className: "word-item__clue--heading" },
                                React.createElement("h4", null, "CLUE"))),
                        wordSearchDetail.words.map((word, index) => (React.createElement(WordItem, { key: index, word: word })))),
                    generatedGrid && (React.createElement("div", { className: "word-form__grid-container" },
                        React.createElement(Grid, { cells: generatedGrid.cells, size: generatedGrid.size })))),
                React.createElement("div", { className: "word-item__controls" },
                    React.createElement("button", { className: "btn--primary", onClick: () => setIsUpdatingWords(true), disabled: wordSearchDetail.status === 'published' }, "Edit Words"))))) : isUpdatingWords ? (React.createElement(React.Fragment, null,
                React.createElement(WordBox, { randomiseGrid: wordSearchDetail.settings.randomiseGrid, words: wordSearchDetail.words, 
                    // seed={
                    //   !wordSearchDetail.settings.randomiseGrid
                    //     ? wordSearchDetail.seed
                    //     : undefined
                    // }
                    onUpdate: handleUpdateWords, onCancel: handleEditWordsCancel }))) : (React.createElement("button", { className: "btn--primary", onClick: () => setIsUpdatingWords(true) }, "Add Words"))))));
};
const mapStateToProps = (state) => {
    return {
        wordSearches: state.wordSearchState.wordSearches,
        wordSearchDetail: state.wordSearchState.wordSearchDetail,
        loading: state.wordSearchState.loading,
        error: state.wordSearchState.error,
        errorDetail: state.wordSearchState.errorDetail,
    };
};
const mapDispatchToProps = {
    getWordSearch,
    updateWordSearch,
    updateWordSearchStatus,
    updateWords,
    deleteWordSearch,
    clearError,
};
export const EditWordSearch = connect(mapStateToProps, mapDispatchToProps)(EditWordSearchComponent);
